import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Power, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * User-managed VAT rate list.  Mirrors CategoryManager visually but with
 * a numeric rate + "varsayılan" star toggle.  Deleting a rate that is
 * referenced by any invoice item degrades to auto-passive so historical
 * snapshots stay untouched (backend enforces the same rule).
 */
export default function VatRateManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/vat-rates");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setRate(""); setOpen(true); };

  const onSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(String(rate).replace(",", "."));
    if (!Number.isFinite(val) || val < 0 || val > 100) {
      toast.error("Oran 0-100 aralığında olmalıdır");
      return;
    }
    setSaving(true);
    try {
      await api.post("/vat-rates", { rate: val });
      toast.success("KDV oranı eklendi");
      setOpen(false);
      setRate("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (r) => {
    const next = r.status === "active" ? "passive" : "active";
    try {
      await api.patch(`/vat-rates/${r.id}/status`, { status: next });
      toast.success(next === "passive" ? "Pasife alındı" : "Aktifleştirildi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const onSetDefault = async (r) => {
    if (r.is_default) return;
    try {
      await api.patch(`/vat-rates/${r.id}/default`);
      toast.success("Varsayılan güncellendi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`%${r.rate} silinsin mi? Kullanılmışsa otomatik olarak Pasif yapılır.`)) return;
    try {
      const { data } = await api.delete(`/vat-rates/${r.id}`);
      toast.success(data.action === "deactivated" ? "Pasife alındı" : "Silindi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const active = (items ?? []).filter((r) => r.status === "active");
  const passive = (items ?? []).filter((r) => r.status === "passive");

  return (
    <SectionCard
      title="KDV Oranları"
      action={
        <Button
          size="sm"
          className="bg-slate-900 hover:bg-slate-800 rounded-lg"
          onClick={openCreate}
          data-testid="add-vat-rate-button"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Yeni Oran
        </Button>
      }
    >
      <div className="p-5 space-y-4" data-testid="vat-rate-manager">
        <p className="text-xs text-slate-500">
          KDV oranları faturalarda satır bazında kullanılır. Kullanılmış oranlar silinmez, otomatik olarak Pasif yapılır.
          Yıldıza tıklayarak varsayılanı değiştirebilirsiniz.
        </p>

        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            Aktif ({active.length})
          </div>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {active.length === 0 && <li className="px-4 py-3 text-sm text-slate-400">Aktif oran yok</li>}
            {active.map((r) => (
              <VatRow key={r.id} r={r} onToggle={onToggle} onSetDefault={onSetDefault} onDelete={onDelete} />
            ))}
          </ul>
        </div>

        {passive.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Pasif ({passive.length})
            </div>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
              {passive.map((r) => (
                <VatRow key={r.id} r={r} onToggle={onToggle} onSetDefault={onSetDefault} onDelete={onDelete} passive />
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setRate(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Yeni KDV Oranı</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="vat-form">
            <div className="space-y-1.5">
              <Label className="text-xs">Oran (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="Örn: 8"
                autoFocus
                className="h-10 rounded-lg"
                data-testid="vat-rate-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving} data-testid="vat-save-button">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function VatRow({ r, onToggle, onSetDefault, onDelete, passive }) {
  return (
    <li
      className={`flex items-center justify-between px-4 py-2.5 text-sm ${passive ? "bg-slate-50/40 text-slate-500" : "bg-white"}`}
      data-testid={`vat-row-${r.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium">%{r.rate}</span>
        {r.is_default && (
          <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
            varsayılan
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="icon"
          variant="ghost"
          className={`h-7 w-7 ${r.is_default ? "text-amber-500" : "text-slate-400 hover:text-amber-500 hover:bg-amber-50"}`}
          onClick={() => onSetDefault(r)}
          disabled={passive}
          data-testid={`vat-default-${r.id}`}
          title={r.is_default ? "Varsayılan" : "Varsayılan yap"}
        >
          <Star className={`w-3.5 h-3.5 ${r.is_default ? "fill-amber-400" : ""}`} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className={`h-7 w-7 ${passive ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}
          onClick={() => onToggle(r)}
          data-testid={`vat-toggle-${r.id}`}
          title={passive ? "Aktifleştir" : "Pasife Al"}
        >
          <Power className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-red-600 hover:bg-red-50"
          onClick={() => onDelete(r)}
          data-testid={`vat-delete-${r.id}`}
          title="Sil"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </li>
  );
}
